// pages/SuggestedQuestions/SuggestedQuestionsSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { 
    Divider,
    Box, 
    Typography, 
    Alert, 
    Paper, 
    TextField, 
    Button, 
    List, 
    ListItem, 
    CircularProgress,
    Tooltip, 
    Switch, 
    FormControl, 
    Select, 
    MenuItem,
    IconButton,
    Container,
    useTheme
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { hasPermission } from '../../utils/permissionUtils';

const MAX_QUESTIONS = 5;
const DELAY_OPTIONS = [30, 60, 90, 120];

export default function SuggestedQuestionsSettings() {

    const theme = useTheme();
    const { chatbotId } = useParams();
    const [questions, setQuestions] = useState([]);
    const [newQuestion, setNewQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [globalDelay, setGlobalDelay] = useState(60);

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('suggested-questions', 'view');
                const modifyPerm = await hasPermission('suggested-questions', 'add|edit');
                const deletePerm = await hasPermission('suggested-questions', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
                setCanDelete(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        document.title = 'Suggested Questions | KlonIT';
        fetchQuestionsAndSettings();
    }, [chatbotId]);

    const fetchQuestionsAndSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const questionsResponse = await axiosInstance.get(`/api/chatbots/${chatbotId}/suggested-questions`);
            setQuestions(questionsResponse.data.data || []);
            
            const settingsResponse = await axiosInstance.get(`/api/chatbots/${chatbotId}/suggested-questions/settings`);
            const delay = settingsResponse.data.data?.suggested_questions_delay || 60;
            setGlobalDelay(delay);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch data.');
        } finally {
            setIsLoading(false);
        }
    }, [chatbotId]);

    const handleGlobalDelayChange = async (newDelay) => {
        try {
            console.log('Updating delay to:', newDelay);
            const response = await axiosInstance.put(`/api/chatbots/${chatbotId}/suggested-questions/global-delay`, {
                delay_seconds: parseInt(newDelay)
            });
            console.log('Update response:', response.data);
            setGlobalDelay(newDelay);
        } catch (err) {
            console.error('Error updating delay:', err.response || err);
            setError(err.response?.data?.message || 'Failed to update delay setting.');
        }
    };

    const handleAddQuestion = async (e) => {
        e.preventDefault();
        if (!newQuestion.trim() || questions.length >= MAX_QUESTIONS) return;

        setIsSubmitting(true);
        setError('');
        try {
            await axiosInstance.post(`/api/chatbots/${chatbotId}/suggested-questions`, {
                question_text: newQuestion,
            });
            setNewQuestion('');
            fetchQuestionsAndSettings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add question.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        setError('');
        try {
            await axiosInstance.delete(`/api/suggested-questions/${questionId}`);
            fetchQuestionsAndSettings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete question.');
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(questions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setQuestions(items);

        try {
            const ordered_ids = items.map(q => q.id);
            await axiosInstance.put(`/api/chatbots/${chatbotId}/suggested-questions/reorder`, {
                ordered_ids
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update question order.');
            fetchQuestionsAndSettings();
        }
    };

    const handleToggleActive = async (questionId, currentState) => {
        try {
            await axiosInstance.put(`/api/suggested-questions/${questionId}/settings`, {
                is_active: !currentState
            });
            fetchQuestionsAndSettings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update question status.');
        }
    };

    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    if (!canView) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 6 }}>
                    <strong>Access Denied</strong>
                    <br />
                    You don't have permission to view suggestion settings. Please contact your administrator.
                </Alert>
            </Container>
        );
    }

    return (
        <Paper
             sx={{
               p: 3,
               mt: 10,
               mx: "auto",
               maxWidth: "1200px",
               bgcolor: "background.paper",
               boxShadow: theme.palette.mode === "dark" ? 4 : 2,
             }}
           >
        
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom  color="textPrimary">
                    Suggested Questions
                </Typography>

                <Divider sx={{ mb: 3, borderColor: "divider" }} />
   

                <Typography variant="body2" color="textPrimary">
                    Add custom questions that will appear to your users before they start chatting.
                </Typography>
            </Box>

            <Paper  sx={{ p: 3, mx: 'auto' }} elevation={1} >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom color="textPrimary">
                        Manage Questions
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                        You can add up to {MAX_QUESTIONS} questions. Drag to reorder. Toggle to activate/deactivate.
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'primary.main' : 'grey.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body1" fontWeight="600" gutterBottom color="textPrimary">
                                    Inactivity Delay
                                </Typography>
                                <Typography variant="body2" color="textPrimary">
                                    Show suggested questions after this many seconds of user inactivity
                                </Typography>
                            </Box>

                            {canModify && (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                        value={globalDelay}
                                        onChange={(e) => handleGlobalDelayChange(e.target.value)}
                                        displayEmpty
                                    >
                                        {DELAY_OPTIONS.map(delay => (
                                            <MenuItem key={delay} value={delay}>
                                                {delay} seconds
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    </Paper>

                    <Box component="form" onSubmit={handleAddQuestion} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter a new suggested question..."
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                disabled={questions.length >= MAX_QUESTIONS || isSubmitting}
                                inputProps={{ maxLength: 500 }}
                            />
                            {canModify && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={!newQuestion.trim() || questions.length >= MAX_QUESTIONS || isSubmitting}
                                    sx={{ minWidth: 80 }}
                                >
                                    {isSubmitting ? <CircularProgress size={20} /> : 'Add'}
                                </Button>
                            )}
                        </Box>
                        {questions.length >= MAX_QUESTIONS && (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                                You have reached the maximum limit of {MAX_QUESTIONS} questions.
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="subtitle2" gutterBottom color="textPrimary">
                        Your Questions ({questions.length}/{MAX_QUESTIONS})
                    </Typography>

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="questions">
                                {(provided) => (
                                    <List
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                                    >
                                        {questions.length > 0 ? (
                                            questions.map((q, index) => (
                                                <Draggable key={q.id} draggableId={String(q.id)} index={index}>
                                                    {(provided) => (
                                                        <ListItem
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            sx={{
                                                                borderBottom: index < questions.length - 1 ? 1 : 0,
                                                                borderColor: 'divider',
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                                                <DragIndicatorIcon sx={{ color: 'textPrimary', cursor: 'grab' }} />
                                                                <Typography
                                                                    sx={{
                                                                        flexGrow: 1,
                                                                        color: !q.is_active ? 'text.disabled' : 'text.primary'
                                                                    }}
                                                                >
                                                                    {q.question_text}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {canModify && (
                                                                        <Tooltip title={q.is_active ? "Deactivate" : "Activate"} arrow>
                                                                            <Switch
                                                                                checked={q.is_active}
                                                                                onChange={() => handleToggleActive(q.id, q.is_active)}
                                                                                color="primary"
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                    {canDelete && (
                                                                        <Tooltip title="Delete Question" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeleteQuestion(q.id)}
                                                                            >
                                                                                <DeleteOutlineIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </ListItem>
                                                    )}
                                                </Draggable>
                                            ))
                                        ) : (
                                            <ListItem>
                                                <Typography color="textPrimary" sx={{ width: '100%', textAlign: 'center' }}>
                                                    No questions added yet.
                                                </Typography>
                                            </ListItem>
                                        )}
                                        {provided.placeholder}
                                    </List>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </Box>
            </Paper>
        </Paper>
    );
}